import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import CategoryService from '../services/CategoryService';
import {
  buildCategoryTree,
  flattenCategoryTree,
  parentSelectOptions,
  slugFromName,
} from '../utils/categoryTree';

const emptyForm = () => ({
  name: '',
  slug: '',
  parentId: '',
  description: '',
  metaTitle: '',
  metaDescription: '',
  seoLevel: 'C',
  isActive: false,
});

const SEO_LEVELS = [
  { value: 'A', label: 'A — Indexation prioritaire' },
  { value: 'B', label: 'B — Indexable' },
  { value: 'C', label: 'C — noindex (défaut)' },
];

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);

  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  const rows = useMemo(() => flattenCategoryTree(tree), [tree]);
  const parentOptions = useMemo(
    () => parentSelectOptions(categories, selectedId),
    [categories, selectedId]
  );

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await CategoryService.list();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Impossible de charger les catégories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const fillForm = (category) => {
    setForm({
      name: category.name || '',
      slug: category.slug || '',
      parentId: category.parentId ?? '',
      description: category.description || '',
      metaTitle: category.metaTitle || '',
      metaDescription: category.metaDescription || '',
      seoLevel: category.seoLevel || 'C',
      isActive: category.isActive !== false,
    });
    setSlugTouched(true);
  };

  const handleSelect = (category) => {
    setSelectedId(category.id);
    fillForm(category);
  };

  const handleNew = () => {
    setSelectedId(null);
    setForm(emptyForm());
    setSlugTouched(false);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: type === 'checkbox' ? checked : value };
      if (name === 'name' && !selectedId && !slugTouched) {
        next.slug = slugFromName(value);
      }
      return next;
    });
    if (name === 'slug') {
      setSlugTouched(true);
    }
  };

  const payloadFromForm = () => ({
    name: form.name.trim(),
    slug: form.slug.trim(),
    parentId: form.parentId === '' ? null : Number(form.parentId),
    description: form.description,
    metaTitle: form.metaTitle,
    metaDescription: form.metaDescription,
    seoLevel: form.seoLevel,
    isActive: form.isActive,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = payloadFromForm();
      if (selectedId) {
        const updated = await CategoryService.update(selectedId, payload);
        toast.success('Catégorie mise à jour');
        await loadCategories();
        if (updated) {
          setSelectedId(updated.id);
          fillForm(updated);
        }
      } else {
        const created = await CategoryService.save(payload);
        toast.success('Catégorie créée');
        await loadCategories();
        if (created) {
          setSelectedId(created.id);
          fillForm(created);
        }
      }
    } catch (err) {
      const message = err.response?.data?.error || 'Enregistrement impossible';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (category) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await CategoryService.update(category.id, {
        isActive: !category.isActive,
      });
      toast.success(category.isActive ? 'Catégorie masquée du site' : 'Catégorie visible sur le site');
      await loadCategories();
      if (selectedId === category.id && updated) {
        fillForm(updated);
      }
    } catch (err) {
      const message = err.response?.data?.error || 'Activation impossible';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) {
      return;
    }
    if (!window.confirm('Supprimer cette catégorie ? Les sous-catégories resteront, sans parent.')) {
      return;
    }
    setSaving(true);
    try {
      await CategoryService.delete(selectedId);
      toast.success('Catégorie supprimée');
      handleNew();
      await loadCategories();
    } catch (err) {
      const message = err.response?.data?.error || 'Suppression impossible';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Catégories</h1>
          <p className="text-surface-400 mt-1">
            Arborescence, visibilité publique et niveau SEO de la marketplace
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={handleNew}>
          <AddIcon className="w-4 h-4" />
          Nouvelle catégorie
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 card p-4">
          <h2 className="text-sm font-medium text-surface-400 mb-3 px-2">Arborescence</h2>
          {loading ? (
            <div className="space-y-2 p-2">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="h-10 bg-surface-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-surface-500 p-3 text-sm">Aucune catégorie pour l'instant.</p>
          ) : (
            <ul className="space-y-1">
              {rows.map((category) => {
                const isSelected = selectedId === category.id;
                const isVisible = category.isActive !== false;
                return (
                  <li key={category.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleSelect(category)}
                      aria-label={category.name}
                      className={`flex-1 min-w-0 text-left rounded-lg px-3 py-2 text-sm transition-colors ${
                        isSelected
                          ? 'bg-accent-500/15 text-white border border-accent-500/30'
                          : 'text-surface-200 hover:bg-surface-800/70'
                      }`}
                      style={{ paddingLeft: `${12 + category.depth * 16}px` }}
                    >
                      <span className="flex items-center gap-2">
                        <CategoryIcon className="w-4 h-4 text-surface-500" />
                        <span className="truncate">{category.name}</span>
                        <span className="ml-auto text-xs text-surface-500" aria-hidden="true">{category.seoLevel}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(category)}
                      disabled={saving}
                      aria-pressed={isVisible}
                      aria-label={isVisible ? `Masquer ${category.name}` : `Afficher ${category.name}`}
                      className={`shrink-0 rounded-md px-2 py-1 text-xs ${
                        isVisible
                          ? 'bg-accent-500/15 text-accent-300'
                          : 'bg-surface-800 text-surface-500'
                      }`}
                    >
                      {isVisible ? 'Active' : 'Masquée'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="lg:col-span-3 card p-6">
          <h2 className="text-lg font-semibold text-white mb-6">
            {selectedId ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="label">Nom</label>
              <input
                id="name"
                name="name"
                className="input"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <label htmlFor="slug" className="label">Slug</label>
              <input
                id="slug"
                name="slug"
                className="input"
                value={form.slug}
                onChange={handleChange}
                readOnly={Boolean(selectedId)}
                aria-describedby="slug-help"
              />
              <p id="slug-help" className="text-xs text-surface-500 mt-1.5">
                {selectedId
                  ? 'Figé après création, pour conserver les URLs publiques.'
                  : 'Généré automatiquement à partir du nom, ajustable avant enregistrement.'}
              </p>
            </div>

            <div>
              <label htmlFor="parentId" className="label">Catégorie parente</label>
              <select
                id="parentId"
                name="parentId"
                className="input"
                value={form.parentId}
                onChange={handleChange}
              >
                <option value="">Aucune (racine)</option>
                {parentOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {'— '.repeat(option.depth)}
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="description" className="label">Description</label>
              <textarea
                id="description"
                name="description"
                className="input min-h-[96px]"
                value={form.description}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="metaTitle" className="label">Meta title</label>
              <input
                id="metaTitle"
                name="metaTitle"
                className="input"
                value={form.metaTitle}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="metaDescription" className="label">Meta description</label>
              <textarea
                id="metaDescription"
                name="metaDescription"
                className="input min-h-[72px]"
                value={form.metaDescription}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="seoLevel" className="label">Niveau SEO</label>
              <select
                id="seoLevel"
                name="seoLevel"
                className="input"
                value={form.seoLevel}
                onChange={handleChange}
              >
                {SEO_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-start gap-3">
              <input
                id="isActive"
                name="isActive"
                type="checkbox"
                className="mt-1"
                checked={form.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive" className="label !mb-0">
                Visible sur le site
                <span className="block text-xs font-normal text-surface-500 mt-1">
                  Une catégorie masquée reste utilisable en admin pour classer les produits.
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between pt-2">
              {selectedId ? (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  <DeleteIcon className="w-4 h-4" />
                  Supprimer
                </button>
              ) : (
                <span />
              )}
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
